import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV } from "@/constants/testIds";
import { useLang } from "@/i18n/LanguageContext";

const LangToggle = ({ className = "", lang, setLang }) => (
  <div className={`inline-flex items-center gap-0.5 border border-black/20 ${className}`}>
    <button
      data-testid={NAV.langEn}
      onClick={() => setLang("en")}
      aria-pressed={lang === "en"}
      className={`px-2.5 py-1 font-overline text-[10px] transition-colors ${
        lang === "en" ? "bg-black text-white" : "text-black/60 hover:text-black"
      }`}
    >
      EN
    </button>
    <button
      data-testid={NAV.langEt}
      onClick={() => setLang("et")}
      aria-pressed={lang === "et"}
      className={`px-2.5 py-1 font-overline text-[10px] transition-colors ${
        lang === "et" ? "bg-black text-white" : "text-black/60 hover:text-black"
      }`}
    >
      EST
    </button>
  </div>
);

export const Header = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (onHome) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${id}`);
      // scroll after navigation
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const sectionLinks = [
    { id: "story", label: t("nav.story"), testid: NAV.links.story },
    { id: "details", label: t("nav.details"), testid: NAV.links.details },
    { id: "schedule", label: t("nav.schedule"), testid: NAV.links.schedule },
    { id: "gallery", label: t("nav.gallery"), testid: NAV.links.gallery },
    { id: "registry", label: t("nav.registry"), testid: NAV.links.registry },
    { id: "faq", label: t("nav.faq"), testid: NAV.links.faq },
    { id: "stay", label: t("nav.stay"), testid: NAV.links.stay },
    { id: "parking", label: t("parking.nav"), testid: NAV.links.parking },
    { id: "contact", label: t("nav.contact"), testid: NAV.links.contact },
  ];

  const closeMenu = () => setOpen(false);

  return (
    <header
      data-testid={NAV.root}
      className={`fixed inset-x-0 top-0 z-50 transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-black/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 flex items-center justify-between h-24 sm:h-28">
        <Link
          data-testid={NAV.logoLink}
          to="/"
          onClick={() => onHome && window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center"
          aria-label="Carmen & Kerbert"
        >
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_bride-groom-hub-75/artifacts/nnvbhv3u_bb644d40-cc12-43ba-9dd5-2e6000e1303a.png"
            alt="Carmen & Kerbert monogram"
            className="h-16 sm:h-20 w-auto object-contain transition-transform duration-500 hover:scale-105"
            style={{ filter: "drop-shadow(0 1px 2px rgba(120,80,15,0.4))" }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
          {sectionLinks.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => goToSection(l.id)}
              className="font-overline text-[10px] xl:text-[11px] text-black/70 hover:text-black transition-colors whitespace-nowrap"
            >
              {l.label}
            </button>
          ))}
          <Link
            data-testid={NAV.links.inspiration}
            to="/inspiration"
            onClick={closeMenu}
            className="font-overline text-[10px] xl:text-[11px] text-[color:var(--sunset-600)] hover:text-black transition-colors whitespace-nowrap"
          >
            {t("nav.inspiration")}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle className="hidden sm:inline-flex" lang={lang} setLang={setLang} />
          <button
            data-testid={NAV.rsvpCta}
            onClick={() => goToSection("rsvp")}
            className="hidden sm:inline-flex items-center font-overline text-[11px] border border-black bg-black text-white hover:bg-[color:var(--sunset-500)] hover:border-[color:var(--sunset-500)] transition-colors px-5 py-2.5"
          >
            {t("nav.rsvp")}
          </button>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-black/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            {sectionLinks.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => goToSection(l.id)}
                className="text-left font-serif text-2xl"
              >
                {l.label}
              </button>
            ))}
            <Link
              data-testid={`${NAV.links.inspiration}-mobile`}
              to="/inspiration"
              onClick={closeMenu}
              className="text-left font-serif text-2xl text-[color:var(--sunset-600)]"
            >
              {t("nav.inspiration")}
            </Link>
            <div className="flex items-center justify-between mt-4">
              <LangToggle lang={lang} setLang={setLang} />
              <button
                data-testid={`${NAV.rsvpCta}-mobile`}
                onClick={() => goToSection("rsvp")}
                className="inline-flex items-center justify-center font-overline text-[11px] border border-black bg-black text-white px-5 py-3"
              >
                {t("nav.rsvp")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
